import { FetchError, type FetchResponse } from "ofetch"
import { defineNuxtPlugin, useRuntimeConfig } from "nuxt/app"
import { captureException } from "@sentry/nuxt"

// The `suppressedStatusCodes` property is added via module augmentation
// in `~/types/ofetch.d.ts`.

export type NuxtFetchRequest = Parameters<typeof $fetch>[0]
export type NuxtFetchOptions = Parameters<typeof $fetch>[1]

type FetchWithRetryOptions = NuxtFetchOptions & {
  /**
   * Number of retry attempts.
   * - `undefined`: uses default `MAX_RETRIES`
   * - `0`: disable retries
   * - `> 0`: retry this many times
   */
  retry?: number
}

type FetchWithRetryArgs = [NuxtFetchRequest, FetchWithRetryOptions?]

function formatFetchRequest(request: NuxtFetchRequest) {
  if (typeof request == "string") {
    return request
  }

  if (request instanceof URL) {
    return request.toString()
  }

  return request.url
}

export type OnRequestHook = (context: {
  request: NuxtFetchRequest
  headers: Headers
  isCsrfOnly?: boolean
}) => void | Promise<void>

export type OnResponseErrorHook = (context: {
  request: NuxtFetchRequest
  response: FetchResponse<unknown>
  options: Pick<FetchWithRetryOptions, "suppressedStatusCodes">
}) => void | Promise<void>

export type OnRequestErrorHook = (context: {
  request: NuxtFetchRequest
  error: Error
}) => void | Promise<void>

export function setupApi({
  csrfEndpoint,
  onRequest,
  onResponseError,
  onRequestError,
}: {
  csrfEndpoint: string
  onRequest?: OnRequestHook
  onResponseError?: OnResponseErrorHook
  onRequestError?: OnRequestErrorHook
}) {
  return defineNuxtPlugin(() => {
    const runtimeConfig = useRuntimeConfig()

    const RETRYABLE = ["Failed to fetch", "Load failed"]
    const RETRY_BASE_DELAY_MS = 1000
    const RETRY_MAX_DELAY_MS = 30_000
    const MAX_RETRIES = 3

    function createBaseFetcher(isCsrfOnly: boolean) {
      return $fetch.create({
        retry: 0,
        baseURL: String(runtimeConfig.public.apiBase),
        credentials: import.meta.client ? "include" : undefined,

        async onRequest({ request, options }) {
          if (import.meta.server) {
            // Cloudflare workers crashes if this is set on server side
            // See https://github.com/cloudflare/workers-sdk/issues/2514
            options.credentials = undefined
          }

          const headers = new Headers(
            options.headers as HeadersInit | undefined,
          )

          if (options.method !== "get" && !headers.has("Accept")) {
            headers.set("Accept", "application/json")
          }

          options.headers = headers

          await onRequest?.({ request, headers, isCsrfOnly })
        },

        async onResponseError({ request, response, options }) {
          // Some users get blocked by Cloudflare on the api.
          // When this happens Cloudflare will return a html page with a captcha.
          // We expect JSON, so this crashes the page.
          //
          // This will redirect them to a page on the API that always serves a challenge.
          // Cloudflare then sets a cookie, so they don't get the challenge again.
          // After solving the challenge, they'll get redirected back.
          const isCloudflareChallenge =
            response.headers.get("cf-mitigated") == "challenge"
          if (isCloudflareChallenge) {
            const redirectUrl = window?.location.href ?? ""
            await navigateTo(
              `https://api.moxso.com/challenge?redirect=${redirectUrl}`,
              { external: true },
            )
            return
          }

          await onResponseError?.({
            request,
            response,
            options,
          })

          if (
            options.suppressedStatusCodes?.includes(response.status) ||
            [401, 403, 419].includes(response.status)
          ) {
            return
          }

          const requestUrl = formatFetchRequest(request)

          captureException(
            new Error(`Fetch error: ${response.status} on ${requestUrl}`),
            {
              extra: {
                status: response.status,
                url: requestUrl,
                responseBody: response._data,
              },
            },
          )
        },
        async onRequestError({ request, error }) {
          await onRequestError?.({
            request,
            error,
          })
        },
      })
    }

    const resolveRetries = (retryOpt: FetchWithRetryOptions["retry"]) => {
      if (typeof retryOpt != "number") {
        return MAX_RETRIES
      }

      return retryOpt
    }

    async function fetchWithRetry<T>(
      fetcher: ReturnType<typeof $fetch.create>,
      ...fetchArgs: FetchWithRetryArgs
    ) {
      const [request, options = {}] = fetchArgs

      const retries = resolveRetries(options.retry)

      const retryDelay = options.retryDelay

      function retryDelayMs(attemptIndex: number) {
        if (typeof retryDelay == "number") {
          return retryDelay
        }

        return Math.min(
          RETRY_BASE_DELAY_MS * 2 ** attemptIndex,
          RETRY_MAX_DELAY_MS,
        )
      }

      let retryErrors: unknown[] = []

      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          if (attempt > 0) {
            const delay = retryDelayMs(attempt - 1)
            await new Promise((r) => setTimeout(r, delay))
          }

          return await fetcher<T>(request, options)
        } catch (err) {
          const isRetryable =
            err instanceof Error &&
            RETRYABLE.some((msg) => err.message.includes(msg))

          if (isRetryable) {
            retryErrors = [...retryErrors, err]
          }

          // Don’t retry if not retryable, or if we’re out of retries
          if (!isRetryable || attempt >= retries) {
            if (retryErrors.length > 0) {
              captureException(
                new Error(`Retry failed for ${formatFetchRequest(request)}`),
                {
                  extra: {
                    retryErrors: retryErrors.map((error) =>
                      error instanceof Error ? error.message : null,
                    ),
                  },
                },
              )
            }
            throw err
          }
        }
      }
    }

    const baseApi = createBaseFetcher(false)
    const baseCsrf = createBaseFetcher(true)

    const api = async function api<T>(...fetchArgs: FetchWithRetryArgs) {
      try {
        return await fetchWithRetry<T>(baseApi, ...fetchArgs)
      } catch (error) {
        const isCsrfError =
          error instanceof FetchError && error.response?.status == 419

        if (isCsrfError) {
          await baseCsrf(csrfEndpoint, { credentials: "include" })
          return await fetchWithRetry<T>(baseApi, ...fetchArgs)
        }

        throw error
      }
    } as unknown as typeof $fetch

    // The `$fetch` option for `useFetch` is typed as `$Fetch`/`typeof $fetch`.
    // If we don't set these, the types won't be correct.
    // ofetch does the same thing:
    // https://github.com/unjs/ofetch/blob/9cf16e098e8ebfe3e353e274b4bc676d2d39f845/src/fetch.ts#L239
    api.raw = baseApi.raw
    api.create = baseApi.create

    const apiCsrfOnly = ((...fetchArgs: FetchWithRetryArgs) =>
      fetchWithRetry(baseCsrf, ...fetchArgs)) as unknown as typeof $fetch
    apiCsrfOnly.raw = baseCsrf.raw
    apiCsrfOnly.create = baseCsrf.create

    return {
      provide: { api, apiCsrfOnly },
    }
  })
}
